import React from 'react'
import PropTypes from 'prop-types'
import {
  IconManager,
  Modal,
  subscriptionsLogoPack,
  ThemeProvider,
  Toasts,
} from '@deseretbook/react-ui'
import { isTrue } from '@deseretbook/cms'
import { DeseretBookCMS, Analytics } from '../../dbcom'
import { Conditional } from '../core'
import { SubscriptionFormManager } from './'
import { SubscriptionFormViews, LoadingView, SIGNUP_VIEWS } from './modal'


IconManager.register(subscriptionsLogoPack)

/**
 * Hook that fetches the subscription signup document from the CMS but only when it's actually
 * needed.
 *
 * @returns  {object} data about the signup document
 */
const useSubscriptionSignupCMSDocument = ({ documentType, documentUID, isInitialized }) => {
  const [isComplete, setCompleted] = React.useState(false)
  const [isLoading, setLoading] = React.useState(false)
  const [detailsDocument, setDetailsDocument] = React.useState({})

  /**
   * Fetches the document from the CMS.
   */
  const getData = async () => {
    setLoading(true)

    // Start by getting critical display data.
    const fetchedDoc = await DeseretBookCMS.getByUID(documentType, documentUID)

    // Fetch the theme too if there is one.
    if (fetchedDoc.data.theme && fetchedDoc.data.theme.id) {
      try {
        const theme = await DeseretBookCMS.themes().getByID(fetchedDoc.data.theme.id)

        fetchedDoc.data.theme = theme
      } catch (error) {
        // unable to get the theme (or there wasn't one)
        fetchedDoc.data.theme = null
      }
    }

    setDetailsDocument(fetchedDoc)
    setLoading(false)
    setCompleted(true)
  }

  React.useEffect(() => {
    if (isInitialized && !isComplete && !isLoading) {
      getData()
    }
  }, [isInitialized])

  return { detailsDocument, isLoading, isComplete }
}

/**
 * Signup Modal for allowing users to signup for subscriptions from anywhere.
 *
 * @returns {function} Component
 */
const SignupModal = ({
  onClose,
  onSuccess,
  user,
  documentUID,
  documentType,
  popupProps,
}) => {
  const [wasOpened, setOpened] = React.useState(popupProps.isOpen)
  const {
    detailsDocument,
    isComplete: detailsDocumentIsFetched,
  } = useSubscriptionSignupCMSDocument({ documentUID, documentType, isInitialized: wasOpened })
  const [isInitialized, setInitialized] = React.useState(detailsDocumentIsFetched)
  const [view, setView] = React.useState(SIGNUP_VIEWS.loading)

  const { data: displayDetails = {} } = detailsDocument

  /**
   * Changes modal view.
   * @param {string} [nextView] - Next view to switch to.
   * @param {boolean} [isInteractive = true] - If the changing of views was caused by user actions.
   */
  const changeView = (nextView = SIGNUP_VIEWS.details, isInteractive = true) => {
    setView(nextView)

    Analytics
      .event({
        category: `Subscription Signup Modal - "${documentUID}"`,
        action: isInteractive
          ? 'User View Transition'
          : 'Automatic View Transition',
        label: `${view} → ${nextView}`,
      }, { isInteractive })
  }

  /**
   * onCreate event handler. Called after the subscription is created. Changes views and calls
   * any `onSuccess` callback.
   *
   * @param {array<object>} [subscriptions = []] - Subscriptions created
   */
  const onCreate = (subscriptions = []) => {
    onSuccess()

    const subscriptionPlanNames = subscriptions
      .map(({ subscriptionPlan }) => subscriptionPlan && subscriptionPlan.name)
      .filter(Boolean)

    const totalNextBillingAmount = subscriptions
      .map(({ nextBillingPeriodAmount }) => nextBillingPeriodAmount)
      .filter(Boolean)
      .reduce((sum, next) => sum + +next, 0)

    Analytics
      .event({
        category: `Subscription Signup Modal - "${documentUID}"`,
        action: 'Successful Signup',
        label: `Subscription Plans Selected: ${subscriptionPlanNames.join(', ')}`,
        value: Number(totalNextBillingAmount.toFixed(2)),
      }, { isInteractive: true })

    if (displayDetails.successSlices && displayDetails.successSlices.length) {
      changeView(SIGNUP_VIEWS.success, false)
    } else {
      onClose()
    }
  }

  /**
   * onCreateFailure event handler. Called after the subscription fails to create.
   *
   * @param {string} error - Error message
   */
  const onCreateFailure = (error) => {
    Toasts.danger('Failed to subscribe', error)

    Analytics
      .event({
        category: `Subscription Signup Modal - "${documentUID}"`,
        action: 'Failed Signup',
        label: error,
      }, { isInteractive: true })
  }

  // Since loading the data for the modal is pretty expensive, we only want to load it once the
  // modal is actually opened for the first time.
  if (!wasOpened && popupProps.isOpen) {
    setOpened(true)
  }

  // If we have details then we can figure out what the first view should be.
  if (detailsDocumentIsFetched && !isInitialized) {
    setInitialized(true)

    if (isTrue(displayDetails.displayDetailsOnOpen) && displayDetails.detailsSlices.length) {
      changeView(SIGNUP_VIEWS.details, false)
    } else {
      changeView(SIGNUP_VIEWS.form, false)
    }
  }

  React.useEffect(() => {
    if (popupProps.isOpen) {
      Analytics
        .event({
          category: `Subscription Signup Modal - "${documentUID}"`,
          action: 'Opened Modal',
        }, { isInteractive: true })
    } else if (wasOpened && !popupProps.isOpen) {
      Analytics
        .event({
          category: `Subscription Signup Modal - "${documentUID}"`,
          action: 'Closed Modal',
        }, { isInteractive: true })
    }
  }, [popupProps.isOpen])

  return (
    <Conditional
      condition={displayDetails.theme}
      component={ThemeProvider}
      props={{
        theme: displayDetails.theme,
      }}
    >
      <Modal
        size="medium"
        close={onClose}
        {...popupProps}
      >
        {!isInitialized && <LoadingView />}
        {isInitialized && (
          <SubscriptionFormManager
            user={user}
            subscriptionType={displayDetails.subscriptionType}
            onCreateSuccessful={onCreate}
            onCreateFailure={onCreateFailure}
            promoCode={displayDetails.promoCode}
            onRefreshAddresses={() => {
              Analytics
                .event({
                  category: `Subscription Signup Modal - "${documentUID}"`,
                  action: 'Created New Shipping Address',
                }, { isInteractive: true })
            }}
            onRefreshPaymentSources={() => {
              Analytics
                .event({
                  category: `Subscription Signup Modal - "${documentUID}"`,
                  action: 'Created New Payment Source',
                }, { isInteractive: true })
            }}
            onAddGiftCard={() => {
              Analytics
                .event({
                  category: `Subscription Signup Modal - "${documentUID}"`,
                  action: 'Applied Gift Card',
                }, { isInteractive: true })
            }}
            onAddGiftCardFailure={(error) => {
              Analytics
                .event({
                  category: `Subscription Signup Modal - "${documentUID}"`,
                  action: 'Failed to Apply Gift Card',
                  label: String(error),
                }, { isInteractive: true })
            }}
            onRemoveGiftCard={() => {
              Analytics
                .event({
                  category: `Subscription Signup Modal - "${documentUID}"`,
                  action: 'Removed Gift Card',
                }, { isInteractive: true })
            }}
            onFormChange={(target = {}) => {
              let label

              if (target.name === 'walletSourceId') {
                label = 'Selected New Wallet Source'
              } else if (target.name === 'subscriptionAddon') {
                label = target.checked
                  ? `Added Subscription Addon: ${target.value}`
                  : `Removed Subscription Addon: ${target.value}`
              } else if (target.name === 'shippingAddressId') {
                label = 'Selected New Shipping Address'
              } else if (target.name === 'btPlanId') {
                label = `Selected New Subscription Plan: ${target.value}`
              }

              if (!label) {
                return
              }

              Analytics
                .event({
                  category: `Subscription Signup Modal - "${documentUID}"`,
                  action: 'Updated Form',
                  label,
                })
            }}
          >
            {subscriptionForm => (
              <SubscriptionFormViews
                subscriptionForm={subscriptionForm}
                user={user}
                currentView={view}
                changeView={changeView}
                detailsDocument={detailsDocument}
              />
            )}
          </SubscriptionFormManager>
        )}
      </Modal>
    </Conditional>
  )
}

SignupModal.propTypes = {
  documentUID: PropTypes.string.isRequired,
  documentType: PropTypes.oneOf([
    'subscription_signup_v1',
  ]).isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
    platinum: PropTypes.bool,
  }),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  popupProps: PropTypes.shape({
    isOpen: PropTypes.bool,
    role: PropTypes.string,
    id: PropTypes.string,
    "aria-modal": PropTypes.string,
    "aria-hidden": PropTypes.string,
    close: PropTypes.func,
  }),
}

SignupModal.defaultProps = {
  user: {},
  onClose: () => {},
  onSuccess: () => {},
}

export default SignupModal
